import { dealInfoHelpers } from 'widget/player-widget';

import { toBIPrice } from 'shared/utils/bi/price';
import URL from 'shared/utils/url';

import UPLOADING_STATUSES from 'shared/constants/uploading-statuses';


const errorMessages = {
  '-7751': 'Invalid video file',
  '-7752': 'File exceeds max size',
  '-1810': 'Unknown error', // this is quota exceeded error but BI don't want to distinguish it
  MAXSIZE: 'File exceeds max size',
};

export function getUploadErrorType(error) {
  return errorMessages[_.toString(error && error.code)] || 'Unknown error';
}

export function getExceedPackageLimitType(error) {
  const args = _(_.get(error, 'data.errorInfo.args'));
  const hasItemTime = _.includes(args, 'item-time');
  const hasTotalTime = _.includes(args, 'total-time');
  const hasTotalSpace = _.includes(args, 'total-space');
  const exceededPackage = hasTotalTime || hasTotalSpace;

  if (_.get(error, 'data.errorInfo.isFree', false)) {
    if (hasItemTime) {
      return 'free_more_than_10';
    }
    if (exceededPackage) {
      return 'free_more_30_min_package';
    }
  } else if (exceededPackage) {
    return 'exceeds_package_limit';
  }

  return 'unknown';
}


const trailerMap = {
  none: 'DontShow',
  'first-20-sec': 'preview',
  trailer: 'traliler',
};

export function getVideoChanges(videoId, before, after) {

  return {
    pricingSelectionChange: hasChanged(isPaid),
    rentalChange: hasChanged(isForRent),
    rentalPricinigChange: hasChanged(rentalPricing),
    rentalPeriodChange: hasChanged(rentalPeriod),
    purchasableChange: hasChanged(isForPurchase),
    purchasePricinigChange: hasChanged(purchasePricing),
    genre: _.toString(after.genre),
    genreChanged: hasChangedField('genre'),
    trailer: trailerMap[after.previewType] || trailerMap.none,
    trailerChanged: hasChangedField('previewType'),
    numOfCastMembers: _.get(after, 'cast.length', 0),
    numOfCrewMembers: _.get(after, 'crew.length', 0),
    videoType: _.toString(after.videoType),
    videoTypeChanged: hasChangedField('videoType'),
    audience: after.contentRating || 'all',
    audienceChanged: hasChangedField('contentRating'),
    posterExists: _.has(after, 'posterUrl'),
    categoryExists: _.get(after, 'categories.length', 0) > 0,
    numOfTags: _.get(after, 'tags.length', 0),
    coverUploaded: _.has(after, 'customCoverUrl'),
    descriptionExists: hasText('description'),
    publisherExists: hasText('publisher'),
    pricingSelection: isPaid(after) ? 'paid' : 'free',
    isRental: isForRent(after),
    rentalPricinig: toBIPrice(rentalPricing(after)),
    isPurchasable: isForPurchase(after),
    rentalPeriod: _.toString(rentalPeriod(after) || 'null'),
    purchasePricinig: toBIPrice(purchasePricing(after)),
    videoID: videoId,
  };

  function hasChanged(fn) {
    return !_.isEqual(fn(before), fn(after));
  }

  function hasChangedField(field) {
    return hasChanged(_.property(field));
  }

  function hasText(field) {
    const val = after[field];
    return _.isString(val) && _.trim(val).length > 0;
  }

  function isForPurchase(videoData) {
    return Boolean(dealInfoHelpers.getSalePriceInfo(videoData.dealInfo));
  }

  function isForRent(videoData) {
    return Boolean(dealInfoHelpers.getRentPriceInfo(videoData.dealInfo));
  }

  function isPaid(videoData) {
    return isForPurchase(videoData) || isForRent(videoData);
  }

  function rentalPricing(videoData) {
    return _.get(dealInfoHelpers.getRentPriceInfo(videoData.dealInfo), 'price', 0);
  }

  function rentalPeriod(videoData) {
    return _.get(dealInfoHelpers.getRentPriceInfo(videoData.dealInfo), 'period', 0);
  }

  function purchasePricing(videoData) {
    return _.get(dealInfoHelpers.getSalePriceInfo(videoData.dealInfo), 'price', 0);
  }
}


// errorScope	= OK/failedChecking/failedRequirement/failedTransmission/failedByUserAction/failedProcessing
// errorType	= OK/networkError/serverError/clientError/userClosedDashboard/userCanceled/quotaExceeded/invalidFile/notSupported/exceedsMaxSize/sessionError/receivedButNotRecognizedError

function getTechnicalErrorScope(uploadingStatus) {
  const map = {
    [UPLOADING_STATUSES.WAITING]: 'failedChecking',
    [UPLOADING_STATUSES.UPLOADING]: 'failedTransmission',
    [UPLOADING_STATUSES.PROCESSING]: 'failedProcessing',
  };
  return map[uploadingStatus] || 'failedChecking';
}

const technicalErrors = [
  'wmp_error.cant_parse_json',
];

const userCancelationErrors = [
  'wmp_error.canceled_by_user',
];

const errorToErrorTypeMap = {
  //SERVER:
  'wpm_error.incorrect_file_info': 'invalidFile',
  'wpm_error.invalid_file': 'invalidFile',
  'wpm_error.unsupported_format': 'notSupported',
  'wpm_error.unsupported_video_codec': 'notSupported',
  'wpm_error.unsupported_audio_codec': 'notSupported',
  'wpm_error.unsupported_file_extension': 'notSupported',
  'wpm_error.file_size_exceeded_limit': 'exceedsMaxSize',
  'wpm_error.mime_type_mismatch': 'invalidFile',
  'wpm_error.invalid_session': 'sessionError',
  'wpm_error.session_expired': 'sessionError',
  'wpm_error.missing_wix_session': 'sessionError',
  'wpm_error.max_video_bitrate_is_exceeded': 'invalidFile',
  'wpm_error.video_duration_mismatch': 'invalidFile',
  //WIXMP:
  'wmp_error.file_size_exceeded_limit': 'exceedsMaxSize',
  'wmp_error.cant_parse_json': 'clientError',
  'wmp_error.canceled_by_user': 'userCanceled',
  //ADAPTER:
  'wmp_error.quota_exceeded': 'quotaExceeded',
};

function getUserErrorScope(uploadingStatus) {
  const map = {
    [UPLOADING_STATUSES.WAITING]: 'failedRequirement',
    [UPLOADING_STATUSES.UPLOADING]: 'failedTransmission',
    [UPLOADING_STATUSES.PROCESSING]: 'failedProcessing',
  };
  return map[uploadingStatus] || 'failedRequirement';
}

function getErrorScope(errorKey, uploadingStatus) {
  if (_.includes(technicalErrors, errorKey)) {
    return getTechnicalErrorScope(uploadingStatus);
  }
  if (_.includes(userCancelationErrors, errorKey)) {
    return 'failedByUserAction';
  }
  if (!errorToErrorTypeMap.hasOwnProperty(errorKey)) {
    return getTechnicalErrorScope(uploadingStatus);
  }
  return getUserErrorScope(uploadingStatus);
}

export function getErrorScopeAndType(isSuccess, response, uploadingStatus) {
  if (isSuccess) {
    return { errorScope: 'OK', errorType: 'OK' };
  }

  const { status, error } = response;
  const errorKey = _.get(error, 'data.errorInfo.key');

  if (errorKey) {
    return {
      errorScope: getErrorScope(errorKey, uploadingStatus),
      errorType: errorToErrorTypeMap[errorKey] || 'receivedButNotRecognizedError',
    };
  }

  // If no errorKey we treat an error as a technical error
  const techScope = getTechnicalErrorScope(uploadingStatus);

  if (status) {
    return { errorScope: techScope, errorType: 'serverError' };
  }

  if (_.isError(response)) {
    return { errorScope: techScope, errorType: 'clientError' };
  }

  return { errorScope: techScope, errorType: 'networkError' };
}

export function getErrorRawDetails(response = {}) {
  const { status, error, url } = response;
  const errorKey = _.get(error, 'data.errorInfo.key');

  const errorRawDetails = {
    errorKey,
    status_code: status,
  };

  const parsedUrl = new URL(url, { skipValidation: true });
  if (!_.includes(parsedUrl.path, 'undefined')) {
    errorRawDetails.path = parsedUrl.path;
  }

  return errorRawDetails;
}



// WEBPACK FOOTER //
// ./src/dashboard/analytics/bi/utils/utils.js